import type { V2AuditScopePayload } from "@/services/api/guardiansv2/types";
import type {
  V2AlertRaisedPayload,
  V2AuditCompletedPayload,
  V2AuditQueuedPayload,
  V2AuditRunningPayload,
  V2AuditSubjectPayload,
  V2AuditTicketRaisedPayload,
} from "@/context/MissionControlWebSocketContext";
import { isJsonObject, optionalNullableString, type JsonValue } from "../jsonValue";

function parseV2AuditScope(value: JsonValue | undefined): V2AuditScopePayload {
  if (!isJsonObject(value)) return {};
  const scope: V2AuditScopePayload = {};
  const mission_id = optionalNullableString(value.mission_id);
  if (mission_id !== undefined) scope.mission_id = mission_id;
  const task_id = optionalNullableString(value.task_id);
  if (task_id !== undefined) scope.task_id = task_id;
  const interaction_id = optionalNullableString(value.interaction_id);
  if (interaction_id !== undefined) scope.interaction_id = interaction_id;
  const agent_id = optionalNullableString(value.agent_id);
  if (agent_id !== undefined) scope.agent_id = agent_id;
  const catalogue_id = optionalNullableString(value.catalogue_id);
  if (catalogue_id !== undefined) scope.catalogue_id = catalogue_id;
  const template_id = optionalNullableString(value.template_id);
  if (template_id !== undefined) scope.template_id = template_id;
  const entity_type = optionalNullableString(value.entity_type);
  if (entity_type !== undefined) scope.entity_type = entity_type;
  const entity_id = optionalNullableString(value.entity_id);
  if (entity_id !== undefined) scope.entity_id = entity_id;
  return scope;
}

function parseV2AuditSubject(value: JsonValue | undefined): V2AuditSubjectPayload | undefined {
  if (!isJsonObject(value)) return undefined;
  const type = value.type;
  const id = value.id;
  if (typeof type !== "string" || typeof id !== "string") return undefined;
  const subject: V2AuditSubjectPayload = { type, id };
  const version = value.version;
  if (version === null || typeof version === "number") subject.version = version;
  return subject;
}

function parseV2AuditLifecyclePayload(
  value: JsonValue | undefined
): V2AuditQueuedPayload | null {
  if (!isJsonObject(value)) return null;
  const audit_id = value.audit_id;
  const audit_type = value.audit_type;
  if (typeof audit_id !== "string" || typeof audit_type !== "string") return null;
  return {
    audit_id,
    audit_type,
    subject: parseV2AuditSubject(value.subject),
    scope: parseV2AuditScope(value.scope),
  };
}

/** Parse ``v2.audit.queued`` payload from a JSON value. */
export function parseV2AuditQueuedPayload(
  value: JsonValue | undefined
): V2AuditQueuedPayload | null {
  return parseV2AuditLifecyclePayload(value);
}

/** Parse ``v2.audit.running`` payload from a JSON value. */
export function parseV2AuditRunningPayload(
  value: JsonValue | undefined
): V2AuditRunningPayload | null {
  return parseV2AuditLifecyclePayload(value);
}

/** Parse ``v2.audit.completed`` payload from a JSON value. */
export function parseV2AuditCompletedPayload(
  value: JsonValue | undefined
): V2AuditCompletedPayload | null {
  const base = parseV2AuditLifecyclePayload(value);
  if (!base || !isJsonObject(value)) return null;
  const rating = value.rating;
  const score = value.score;
  const ticket_count = value.ticket_count;
  return {
    ...base,
    rating: rating === null || typeof rating === "string" ? rating : null,
    score: score === null || typeof score === "number" ? score : null,
    ticket_count: typeof ticket_count === "number" ? ticket_count : 0,
  };
}

/** Parse ``v2.audit.ticket_raised`` payload from a JSON value. */
export function parseV2AuditTicketRaisedPayload(
  value: JsonValue | undefined
): V2AuditTicketRaisedPayload | null {
  if (!isJsonObject(value)) return null;
  const ticket_id = value.ticket_id;
  const ticket_ref = value.ticket_ref;
  const severity = value.severity;
  const type = value.type;
  const source_guardian = value.source_guardian;
  if (
    typeof ticket_id !== "string" ||
    typeof ticket_ref !== "string" ||
    typeof severity !== "string" ||
    typeof type !== "string" ||
    typeof source_guardian !== "string"
  ) {
    return null;
  }
  return {
    ticket_id,
    ticket_ref,
    severity,
    type,
    source_guardian,
    subject: parseV2AuditSubject(value.subject),
    scope: parseV2AuditScope(value.scope),
  };
}

/** Parse ``v2.alert.raised`` payload from a JSON value. */
export function parseV2AlertRaisedPayload(
  value: JsonValue | undefined
): V2AlertRaisedPayload | null {
  if (!isJsonObject(value)) return null;
  const alert_id = value.alert_id;
  const source = value.source;
  const title = value.title;
  const summary = value.summary;
  if (typeof alert_id !== "string" || typeof source !== "string" || typeof title !== "string" || typeof summary !== "string") {
    return null;
  }
  const description = optionalNullableString(value.description);
  const category = optionalNullableString(value.category);
  const audience = optionalNullableString(value.audience);
  const next_step = optionalNullableString(value.next_step);
  const notify = value.notify === true;
  return {
    alert_id,
    source,
    title,
    summary,
    ...(description !== undefined ? { description } : {}),
    ...(category !== undefined ? { category } : {}),
    ...(audience !== undefined ? { audience } : {}),
    ...(next_step !== undefined ? { next_step } : {}),
    notify,
    subject: parseV2AuditSubject(value.subject),
    scope: parseV2AuditScope(value.scope),
  };
}
